import { delegateEvent, UxplComponent } from '../core';

import { Attributes, OptionClasses, OptionEqualityFn } from './shared';

interface ListboxOption {
  text: string;
  value?: string;
  disabled?: boolean;
}

interface ListboxEventMap {
  selection: ListboxOption;
  active: { element: Element; option: ListboxOption };
}

let id = 0;

export class Listbox extends UxplComponent<ListboxEventMap> {
  private isOptionEqual: OptionEqualityFn;
  private ref: Element;
  private selected: ListboxOption;
  private options: ListboxOption[];
  private optionMap = new Map<string, ListboxOption>();
  private activeIndex = -1;
  private uniqueId: string;
  private emptyMessage: string;

  constructor(
    ref: Element,
    config?: {
      isOptionEqual?: OptionEqualityFn;
      emptyMessage?: string;
      options?: ListboxOption[];
      selected?: ListboxOption;
    }
  ) {
    super();
    this.ref = ref;
    config = config || {};
    this.options = config.options || [];
    this.selected = config.selected;
    this.isOptionEqual = config.isOptionEqual;
    this.emptyMessage = config.emptyMessage;
    this.uniqueId = `${id++}`;
    this.ref.setAttribute('role', 'listbox');
    this.init();
  }

  getSelected(): ListboxOption {
    return this.selected;
  }

  setSelected(selectedOption: ListboxOption): void {
    this.selected = selectedOption;
    this.queryOptionElements().forEach((node) => {
      node.classList.remove(OptionClasses.IsSelected);
      node.setAttribute('aria-selected', 'false');
      if (
        this.isOptionEqual(
          this.optionMap.get(node.getAttribute('id')),
          selectedOption
        )
      ) {
        node.classList.add(OptionClasses.IsSelected);
        node.setAttribute('aria-selected', 'true');
      }
    });
  }

  setOptions(options: ListboxOption[]): void {
    this.activeIndex = -1;
    this.options = options;
    this.renderOptions();
  }

  isDisplayable(): boolean {
    return (this.options && this.options.length > 0) || !!this.emptyMessage;
  }

  handleKey(event: KeyboardEvent): void {
    const prevActive = this.getOptionElementAt(this.activeIndex);
    let currentActive = prevActive;

    switch (event.key) {
      case 'Up':
      case 'ArrowUp':
        if (this.activeIndex > 0) {
          const nextActive = this.getNextActive('up');
          if (nextActive) {
            this.activeIndex = nextActive.index;
            currentActive = nextActive.element;
          }
        }
        event.preventDefault();
        break;
      case 'Down':
      case 'ArrowDown':
        if (this.activeIndex < this.optionMap.size) {
          const nextActive = this.getNextActive('down');
          if (nextActive) {
            this.activeIndex = nextActive.index;
            currentActive = nextActive.element;
          }
        }
        event.preventDefault();
        break;
      case 'Enter':
        this.handleClick({
          element: this.getOptionElementAt(this.activeIndex),
          event,
        });
        event.preventDefault();
        return;
    }
    if (prevActive === currentActive) {
      return;
    }

    if (prevActive) {
      prevActive.classList.remove(OptionClasses.IsActive);
    }
    if (currentActive) {
      currentActive.classList.add(OptionClasses.IsActive);
    }
    this.eventDispatcher.dispatchEvent('active', {
      element: currentActive,
      option: this.optionMap.get(currentActive.getAttribute('id')),
    });
  }

  destroy(): void {
    super.destroy();
    this.optionMap.clear();
  }

  private getOptionElementAt(index: number): Element {
    return this.queryOptionElements()[index];
  }

  private queryOptionElements(): NodeListOf<Element> {
    return this.ref.querySelectorAll(`[${Attributes.Option}]`);
  }

  private getNextActive(direction: 'up' | 'down'): {
    index: number;
    element: Element;
  } {
    const elems = this.queryOptionElements();
    let element;
    let i = this.activeIndex;
    do {
      i = i + (direction === 'up' ? -1 : 1);
      element = elems[i];
      if (element && element.getAttribute('aria-disabled') !== 'true') {
        return { index: i, element };
      }
    } while (element);
    return null;
  }

  private handleClick({ element, event }: { element: Element; event: Event }) {
    const option = this.optionMap.get(element.getAttribute('id'));
    if (option && !option.disabled) {
      this.eventDispatcher.dispatchEvent('selection', option);
      event.preventDefault();
    }
  }

  private renderOptions() {
    const fragment = document.createDocumentFragment();
    this.optionMap.clear();
    this.clearOptions();
    if (this.options && this.options.length > 0) {
      this.options.forEach((option, index) => {
        const nodeId = this.makeOptionNodeId(`${index}`);
        const optionNode = this.createOptionNode(option, nodeId);
        this.optionMap.set(nodeId, option);
        fragment.appendChild(optionNode);
      });
    } else if (this.emptyMessage) {
      fragment.appendChild(this.createEmptyMessageNode());
    }
    this.ref.appendChild(fragment);
  }

  private makeOptionNodeId(uniqueId: string) {
    return `pl-combobox-option-c${this.uniqueId}-${uniqueId}`;
  }

  private createOptionNode(
    option: ListboxOption,
    optionUniqueId: string
  ): Element {
    const optionNode = document.createElement('li');
    optionNode.setAttribute('id', optionUniqueId);
    optionNode.innerHTML = option.text;
    optionNode.setAttribute(Attributes.Option, '');
    optionNode.setAttribute('data-pl-combobox-option-value', option.value);
    optionNode.setAttribute('role', 'option');
    optionNode.classList.add(OptionClasses.Option);
    if (this.selected && this.isOptionEqual(this.selected, option)) {
      optionNode.classList.add(OptionClasses.IsSelected);
      optionNode.setAttribute('aria-selected', 'true');
    }
    if (option.disabled) {
      optionNode.setAttribute('aria-disabled', 'true');
      optionNode.classList.add(OptionClasses.IsDisabled);
    }
    return optionNode;
  }

  private createEmptyMessageNode() {
    const node = document.createElement('li');
    node.innerHTML = this.emptyMessage;
    node.classList.add(OptionClasses.EmptyMessage);
    return node;
  }

  private clearOptions() {
    while (this.ref.firstChild) {
      this.ref.removeChild(this.ref.firstChild);
    }
  }

  private init(): void {
    this.eventDisposers.push(
      delegateEvent(
        this.ref,
        'click',
        '[data-pl-combobox-option]',
        (element, event) => {
          event.preventDefault();
          this.handleClick({ element, event });
        }
      )
    );
    this.renderOptions();
  }
}
